import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, OnInit, signal, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { debounceTime } from 'rxjs';
import { Table } from 'primeng/table';
import { Select } from 'primeng/select';
import { DecimalPipe } from '@angular/common';

import { TableTopLegendVm } from 'app/shared/table/table-top/table-top-legend/table-top-legendVm';
import { BA_BudgetVm } from 'app/shared/generated/BA/Models/BA_BudgetVm';
import { BA_BudgetFyFilterVm } from 'app/shared/generated/BA/Models/BA_BudgetFyFilterVm';
import { BAService } from '../../ba.service';
import { SystemMessage } from 'app/core/system-message/system-message-service';
import { validateForm } from 'app/shared/form-elements/form-validateForm.function';
import { GlobalService } from 'app/shared/services/global.service';
import { SharedTableModule } from 'app/shared/standalone/shared-table/shared-table.module';
import { AvailableColumns, VisibleColsService } from 'app/shared/standalone/shared-table/visible-cols.service';
import { LazyTableInput } from 'app/shared/standalone/shared-table/lazy-table-input.interface';
import { LazyTableService } from 'app/shared/standalone/shared-table/lazy-table.service';
import { LazyLoadVm } from 'app/shared/standalone/shared-table/lazy-load-vm.interface';

@Component({
    selector: 'pcg-ba-budget-list'
    , templateUrl: './budget-list.component.html'
    , styleUrls: ['./budget-list.component.scss']
    , standalone: true
    , providers: [ DecimalPipe ]
    , host: { '(window:resize)': 'onResize()' }
    , changeDetection: ChangeDetectionStrategy.Eager
    , imports: [
        SharedTableModule
        , Select
    ]
})
export class BudgetListComponent implements OnInit {

    @ViewChild("modalNewBudget") modalNewBudget: TemplateRef<any>;
    @ViewChild("modalFunds") modalFunds: TemplateRef<any>;

    httpClient = inject(HttpClient);
    modalService = inject(NgbModal);
    router = inject(Router);
    baService = inject(BAService);
    toastr = inject(ToastrService);
    route = inject(ActivatedRoute);
    decimalPipe = inject(DecimalPipe);
    lts = inject(LazyTableService);
    cdr = inject(ChangeDetectorRef);

    columnDefs = BA_BudgetVm.ColumnDefs;
    lazyLoadVm: LazyLoadVm;
    filterFiscalYears: BA_BudgetFyFilterVm[] = [];
    budgetList: BA_BudgetVm[] = [];
    availableColumns!: AvailableColumns[];
    visibleColumns!: AvailableColumns[];
    searchColumns!: string[];    
    formGroup = BA_BudgetVm.Form;

    budgetBelowColor: string = '#FFC0CB';
    poBelowColor: string = '#fcbe89';
    bothBelowColor: string = '#ff7f7f';
    customExcelUrl = "api/BA/GetBudgetListExcel";
    baseUrl = this.route.snapshot['_routerState'].url.split('/')[0];

    MAX_DISPLAY_FUNDS = 3;
    selectedRowFunds = signal<string[]>([]);

    allocationTotal: number = 0;
    expenituresTotal: number = 0;
    availableBudgetTotal: number = 0;
    currentPoValueTotal: number = 0;
    poBalanceTotal: number = 0;

    isMobile = false;
    isLoading: boolean = true;
    searchValue: string | undefined;

    filters = {
		isActive: true || 'true'
        , fiscalYearId: null
	};
	initFilters = Object.assign({}, this.filters);

    legend: TableTopLegendVm[] = [
		{
			id: 0
            , colorCode: this.budgetBelowColor
            , description: 'Available Budget Funds are below notification levels'
		}
        , {
			id: 1
            , colorCode: this.poBelowColor
            , description: 'Available PO Funds are below notification levels'
		}
        , {
			id: 2
            , colorCode: this.bothBelowColor
            , description: 'Available Budget and PO Funds are below notification levels'
		}
	];

    tableDts = {
        root: {
            header: {
                lgPadding: "0 0 0.5rem 0"
            }
        }
    };

    lazyTableInput: LazyTableInput = {
        pageNumber: 1
        , pageSize: 100
        , sortField: "budgetName"
        , sortOrder: 1
    };

    constructor(title: Title) { title.setTitle("Budget Tracking"); }

    onResize() { this.isMobile = GlobalService.setIsMobile(window.innerWidth); }

    ngOnInit() { 
        this.isMobile = GlobalService.setIsMobile(window.innerWidth); 
        this.getFilters();         
    }

    navToPOs(budgetId: number) { this.router.navigate([`/ba/budgets/pos/list/${budgetId}`]); }
    editBudget(budgetId: number) { this.router.navigateByUrl(`ba/budgets/details/${budgetId}`); }
    openBudgetModal = () => this.modalService.open(this.modalNewBudget, {animation: false});
    exportList() { this.httpClient.get(this.customExcelUrl).subscribe((output: any) => { window.open(output); }); }
    onFilterChange(event: any) { this.calculateTotals(event.filteredValue); }

    getFilters() { 
        this.baService.getBaFiscalYears().subscribe((lst: any)=> { 
            this.filterFiscalYears = lst; 
            this.filterFiscalYears.unshift({
                value: null
                , text: "All"
                , isCurrent: false
            });
            var fyId = lst?.find(o => o.isCurrent)?.value!;
            if (fyId != null) { this.filters.fiscalYearId = fyId; }
            this.getList();
        }); 
    }

    getList() { 
        this.httpClient.get(`api/BA/GetBudgetList` 
            + `?isActive=${this.filters.isActive}`
            + `&fiscalYearId=${this.filters.fiscalYearId}`
        ).subscribe((lst: BA_BudgetVm[]) => { 
            this.budgetList = lst;
            this.availableColumns = VisibleColsService.getAvailableCols(this.columnDefs);
            this.visibleColumns = this.availableColumns.filter(o => o.isVisible);
            this.searchColumns = this.availableColumns.filter(o => o.canSearch).map(o => o.field);
            this.calculateTotals(this.budgetList);
            this.isLoading = false;
            this.setExcelUrl();
        }); 
    }

    columnChange(event: any) {
        this.visibleColumns = event;
        this.visibleColumns = event.sort((a, b) => {
            return this.availableColumns.findIndex(p => p.field === a.field) 
                - this.availableColumns.findIndex(p => p.field === b.field);
        });
    }

    clearFilters(
        table: Table
        , el: any
    ) {
        table.clear();
        this.searchValue = '';
        el.value = "";
        this.filters = Object.assign(this.filters, this.initFilters);
        this.getList();
    }

    getRowColor(row: BA_BudgetVm) {
		if (
            row.isBudgetBelowNotificationLvl 
            && row.isPOBelowNotificationLvl
        ) {	return this.bothBelowColor; }
		if (row.isBudgetBelowNotificationLvl) { return this.budgetBelowColor; }
        if (row.isPOBelowNotificationLvl) { return this.poBelowColor; }		
		return "none";
	}

    setExcelUrl() {
		this.customExcelUrl = `api/BA/GetBudgetListExcel` 
            + `?isActive=${this.filters.isActive ?? ""}` 
            + `&fiscalYearId=${this.filters.fiscalYearId ?? ""}`;
	}

    saveBudget(
        row: BA_BudgetVm
        , isNew?: boolean
    ) {
        this.httpClient.put('api/BA/SaveBudget', row)
            .pipe(debounceTime(300))
            .subscribe((msg: SystemMessage) => {
                if (!msg.isSuccessful) {
                    this.toastr.error(
                        `There was a problem saving the budget. Please contact a system administrator. Error: ${msg.message}`
                        , null
                        , { positionClass: 'toast-top-right', timeOut: 2000 }
                    );
                } else {
                    this.toastr.success(`${msg.message}`, null, { positionClass: 'toast-top-right', timeOut: 2000 });
                    if (this.baService.budgetSignal()) { this.baService.budgetSignal.set(msg.model); }
                    if (isNew) {
                        this.modalService.dismissAll();
                        this.editBudget(msg.model.budgetId);
                    } else { this.getList(); }
                }
            }
        );
    }

    openFunds = (funds: string[]) => {
        this.selectedRowFunds.set(funds);
        this.modalService.open(this.modalFunds, {animation: false})
    }

    calculateTotals(dt: any) {
        this.allocationTotal = 0;
        this.expenituresTotal = 0;
        this.availableBudgetTotal = 0;
        this.currentPoValueTotal = 0;
        this.poBalanceTotal = 0;

        for (let budget of dt) {
            this.allocationTotal += budget.allocatedAmt;
            this.expenituresTotal += budget.expenditures;
            this.availableBudgetTotal += budget.availableBudget;
            this.currentPoValueTotal += budget.currentPOsValue;
            this.poBalanceTotal += budget.pOsRemainingBalance;
        }
    }

    createBudget() {
        if (this.formGroup.value.fiscalYearId == 0) {
            var href = `${this.baseUrl}/ba/admin/fiscal-years`;
            var message = `<a href="${href}" target="_blank">Click Here</a> to add a new fiscal year.`;
            this.toastr.error(
                message
                , null
                , { 
                    positionClass: 'toast-top-right'
                    , disableTimeOut: true
                    , closeButton: true
                    , enableHtml: true
                    , tapToDismiss: false
                }
            );
            return;
        }
        if (
            this.formGroup.value.budgetName == null 
            || this.formGroup.value.budgetName == ""
        ) { 
            validateForm(this.formGroup)
            return; 
        }
        // Set date created to today, was causing errors if not set
        this.formGroup.controls.dateCreated.setValue(new Date().toISOString());
        // By default make budget active
        this.formGroup.controls.isActive.setValue(true);

        // By Default set low budget level to 10%
        this.formGroup.controls.isLowBudgetLevelPercent.setValue(true);
        this.formGroup.controls.lowBudgetLevel.setValue(10);
        this.saveBudget(this.formGroup.value, true);
    }
}
